/**
 * Rashi display data, keyed by the ENGINE's canonical ids.
 *
 * The engine's `moon_rashi` ids are HINDI (`singh`, never `leo`) — rule 29 #2's
 * display-name vs id trap, recorded as `lesson_engine_rashi_ids_are_hindi`. This
 * lives in its own module so the next consumer resolves through the same
 * normalizer instead of re-keying its own table and reintroducing the miss.
 */

// Keyed by the ENGINE's canonical rashi ids, which are Hindi (rule 29 #2 —
// `moon_rashi` returns `singh`, never `leo`). Keying this by English names meant
// every lookup missed; see `resolveRashiInfo` for why that was invisible.
const RASHI_DATA: Record<string, { hi: string; symbol: string; ruler: string; element: string }> = {
  mesh: { hi: "मेष", symbol: "♈", ruler: "Mars", element: "Fire" },
  vrishabh: { hi: "वृषभ", symbol: "♉", ruler: "Venus", element: "Earth" },
  mithun: { hi: "मिथुन", symbol: "♊", ruler: "Mercury", element: "Air" },
  kark: { hi: "कर्क", symbol: "♋", ruler: "Moon", element: "Water" },
  singh: { hi: "सिंह", symbol: "♌", ruler: "Sun", element: "Fire" },
  kanya: { hi: "कन्या", symbol: "♍", ruler: "Mercury", element: "Earth" },
  tula: { hi: "तुला", symbol: "♎", ruler: "Venus", element: "Air" },
  vrishchik: { hi: "वृश्चिक", symbol: "♏", ruler: "Mars", element: "Water" },
  dhanu: { hi: "धनु", symbol: "♐", ruler: "Jupiter", element: "Fire" },
  makar: { hi: "मकर", symbol: "♑", ruler: "Saturn", element: "Earth" },
  kumbh: { hi: "कुंभ", symbol: "♒", ruler: "Saturn", element: "Air" },
  meen: { hi: "मीन", symbol: "♓", ruler: "Jupiter", element: "Water" },
};

// English aliases stay accepted so a non-engine caller (or a future response
// shape) still resolves rather than silently degrading.
const RASHI_ALIASES: Record<string, string> = {
  aries: "mesh",
  taurus: "vrishabh",
  gemini: "mithun",
  cancer: "kark",
  leo: "singh",
  simha: "singh",
  virgo: "kanya",
  libra: "tula",
  scorpio: "vrishchik",
  sagittarius: "dhanu",
  capricorn: "makar",
  aquarius: "kumbh",
  pisces: "meen",
};

function canonicalRashiId(moonRashi?: string | null): string | undefined {
  const raw = (moonRashi ?? "").trim().toLowerCase();
  if (!raw) return undefined;
  const id = RASHI_ALIASES[raw] ?? raw;
  return id in RASHI_DATA ? id : undefined;
}

/**
 * Does the engine's `moon_rashi` name a rashi at all?
 *
 * This is the ENGINE CONTRACT question, deliberately separate from
 * `resolveRashiInfo` below, which is the enrichment question. Collapsing the two
 * into one nullable lookup is what let a single check answer both — and it
 * answers them in opposite directions: a missing decoration must never withhold
 * monetization (rule 69), while an unreadable engine result must never be
 * monetized at all (rules 29/44 — the engine is the author of truth). Keep them
 * apart so a future gap in the display table cannot silently re-acquire the
 * power to gate revenue.
 */
export function isKnownRashi(moonRashi?: string | null): boolean {
  return canonicalRashiId(moonRashi) !== undefined;
}

/** Display decorations — symbol, ruling planet, element. Cosmetic only. */
export function resolveRashiInfo(moonRashi?: string | null) {
  const id = canonicalRashiId(moonRashi);
  return id === undefined ? undefined : RASHI_DATA[id];
}

export type RashiInfo = (typeof RASHI_DATA)[string];
